import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSSE, SSEEvent } from '../hooks/useSSE';
import { getUnreadSummary } from '../api';
import { UnreadSummary } from '../types';

/** unread-changed 事件携带的已读回执：标明哪些条目被（其他窗口/设备）标记为已读 */
export interface ReadReceipt {
  kind?: 'notification' | 'announcement';
  ids?: string[];
  all?: boolean;
}

/** SSE 是外部数据边界，负载先做运行时校验再进 state（形状不对则整条忽略） */
function isReadReceipt(value: unknown): value is ReadReceipt {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  if (o.kind !== undefined && o.kind !== 'notification' && o.kind !== 'announcement') return false;
  if (o.ids !== undefined && !(Array.isArray(o.ids) && o.ids.every(id => typeof id === 'string'))) return false;
  if (o.all !== undefined && typeof o.all !== 'boolean') return false;
  return true;
}

interface NotificationContextValue {
  unread: UnreadSummary;
  refreshUnread: () => void;
  latestEvent: SSEEvent | null;
  clearLatestEvent: () => void;
  /** 其他 tab/设备标记已读时递增，列表页据此静默重拉数据 */
  readSyncVersion: number;
  /** 最近一次已读回执（每次事件都是新对象引用），气泡据此精确关闭被读条目 */
  latestReadReceipt: ReadReceipt | null;
}

const defaultUnread: UnreadSummary = { announcements: 0, notifications: 0, total: 0 };

const NotificationContext = createContext<NotificationContextValue>({
  unread: defaultUnread,
  refreshUnread: () => {},
  latestEvent: null,
  clearLatestEvent: () => {},
  readSyncVersion: 0,
  latestReadReceipt: null,
});

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [unread, setUnread] = useState<UnreadSummary>(defaultUnread);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('token'));
  const [latestEvent, setLatestEvent] = useState<SSEEvent | null>(null);
  const [readSyncVersion, setReadSyncVersion] = useState(0);
  const [latestReadReceipt, setLatestReadReceipt] = useState<ReadReceipt | null>(null);

  // 监听 storage 变化（跨 tab 登录/登出同步）
  useEffect(() => {
    const handler = () => setIsLoggedIn(!!localStorage.getItem('token'));
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const refreshUnread = useCallback(() => {
    if (!localStorage.getItem('token')) return;
    getUnreadSummary()
      .then(setUnread)
      .catch(() => setUnread(defaultUnread));
  }, []);

  // 页面加载时拉取一次未读数
  useEffect(() => {
    if (isLoggedIn) {
      refreshUnread();
    }
  }, [isLoggedIn, refreshUnread]);

  // SSE 事件处理：收到新消息时从服务端拉取最新未读数（避免本地计数器漂移）
  const handleSSEEvent = useCallback((event: SSEEvent) => {
    if (event.type === 'announcement' || event.type === 'notification') {
      setLatestEvent(event);
      refreshUnread();
    } else if (event.type === 'unread-changed') {
      // 其他 tab/设备标记已读 → 刷新未读数，不弹新消息提示；
      // 回执校验通过后透传给气泡（展开成新对象，保证每次事件都触发订阅方 effect）；
      // 形状非法则忽略回执（未读数刷新不受影响，气泡保留属安全降级）
      refreshUnread();
      setReadSyncVersion(v => v + 1);
      if (isReadReceipt(event.data)) {
        setLatestReadReceipt({ ...event.data });
      }
    }
  }, [refreshUnread]);

  useSSE({ onEvent: handleSSEEvent, onReconnect: refreshUnread, enabled: isLoggedIn });

  const clearLatestEvent = useCallback(() => {
    setLatestEvent(null);
  }, []);

  return (
    <NotificationContext.Provider value={{ unread, refreshUnread, latestEvent, clearLatestEvent, readSyncVersion, latestReadReceipt }}>
      {children}
    </NotificationContext.Provider>
  );
}
